import { findActionLinkPage } from '@shared/actionLinks/pages.js'

// Query params removed entirely from URLs before sending to Datadog. The LP
// quiz answer (lp_pain_location) is health data: app start removes it from
// the address bar, but RUM reports the page load as a document resource with
// the URL from the browser's navigation timing, which still carries it.
export const REMOVED_PARAMS = ['email', 'lp_pain_location']

// Params whose key contains this substring keep only their first/last 4 chars,
// so values remain loosely matchable against backend data.
export const PARTIAL_REDACT_KEY_SUBSTRING = 'token'

const PARTIAL_REDACT_MIN_LENGTH = 13

// Base for parsing relative URLs; recognisable in output if it ever leaks.
const RELATIVE_BASE = 'http://relative.invalid'

/**
 * Removes or partially redacts sensitive query params from a URL.
 * Handles absolute and relative URLs; returns the input unchanged when there
 * is nothing to do or the URL cannot be parsed (must never throw — it runs
 * inside Datadog beforeSend hooks).
 */
export function redactSensitiveUrlParams(url) {
  if (typeof url !== 'string' || !url.includes('?')) return url

  let parsed
  try {
    parsed = new URL(url, RELATIVE_BASE)
  } catch {
    return url
  }

  // Set dedupes repeated keys (?token=a&token=b) so we never process a key
  // we already deleted; matching is case-insensitive, mutation keeps the
  // original key name.
  for (const key of new Set(parsed.searchParams.keys())) {
    const lowerKey = key.toLowerCase()
    if (REMOVED_PARAMS.includes(lowerKey)) {
      parsed.searchParams.delete(key)
    } else if (lowerKey.includes(PARTIAL_REDACT_KEY_SUBSTRING)) {
      const value = parsed.searchParams.get(key)
      if (value.length < PARTIAL_REDACT_MIN_LENGTH) {
        // Too short to mask meaningfully without exposing most of the value.
        parsed.searchParams.delete(key)
      } else {
        parsed.searchParams.set(
          key,
          `${value.slice(0, 4)}****${value.slice(-4)}`,
        )
      }
    }
  }

  return parsed.href.startsWith(RELATIVE_BASE)
    ? parsed.href.slice(RELATIVE_BASE.length)
    : parsed.href
}

const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"']+/g

/**
 * Redacts sensitive query params inside URLs embedded in free text, e.g.
 * Datadog network-error log messages like "Fetch error GET <url>".
 */
export function redactSensitiveUrlParamsInText(text) {
  if (typeof text !== 'string' || !text.includes('?')) return text
  return text.replace(URL_IN_TEXT_PATTERN, (url) =>
    redactSensitiveUrlParams(url),
  )
}

const TOKEN_PATTERN = /[^\s"']+/g

// An action link page carries its credential in the fragment. The app removes it from the address
// bar at startup, but the browser keeps the original URL in the navigation timing entry, which
// Datadog reports as the document resource. Each whitespace/quote-delimited token is inspected
// once, so the scan stays linear and no query length can let a fragment through.
function stripActionLinkFragments(text) {
  if (!text.includes('#')) return text
  return text.replace(TOKEN_PATTERN, (token) => {
    const hashIndex = token.indexOf('#')
    if (hashIndex === -1) return token

    const beforeHash = token.slice(0, hashIndex)
    // Every "?" segment is checked, not just the path: an action link URL can be embedded unencoded
    // in another URL's query, with or without a query of its own.
    return beforeHash.split('?').some((segment) => findActionLinkPage(segment))
      ? beforeHash
      : token
  })
}

/**
 * Scrubs every string field of an event in place, so Datadog beforeSend
 * hooks need no per-field enumeration. The RUM SDK discards mutations of
 * non-modifiable fields on its own.
 */
export function redactSensitiveUrlsDeep(obj) {
  if (obj == null || typeof obj !== 'object') return
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      obj[key] = redactSensitiveUrlParamsInText(stripActionLinkFragments(value))
    } else if (value && typeof value === 'object') {
      redactSensitiveUrlsDeep(value)
    }
  }
}
